import React, { useState, useEffect, useCallback } from 'react'
import { FileUp, Search, Calendar, X } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../config/api'

const P = { pri: '#0a5169', priM: '#e8f2f6' }
const fmt$ = (n) => '$' + (Number(n) ?? 0).toLocaleString()

export default function LinkTransactionModal({ caseId, isOpen, onClose, onAdded }) {
  const [tab, setTab] = useState('browse')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [preset, setPreset] = useState('1month')
  const [items, setItems] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [csvFile, setCsvFile] = useState(null)
  const [csvPreview, setCsvPreview] = useState([])
  const [error, setError] = useState(null)

  const applyPreset = useCallback((p) => {
    setPreset(p)
    const d = new Date()
    const to = d.toISOString().slice(0, 10)
    let from
    if (p === '7days') {
      d.setDate(d.getDate() - 7)
      from = d.toISOString().slice(0, 10)
    } else if (p === '2weeks') {
      d.setDate(d.getDate() - 14)
      from = d.toISOString().slice(0, 10)
    } else {
      d.setMonth(d.getMonth() - 1)
      from = d.toISOString().slice(0, 10)
    }
    setDateFrom(from)
    setDateTo(to)
  }, [])

  useEffect(() => {
    if (preset && isOpen) applyPreset(preset)
  }, [preset, isOpen])

  const loadCombined = useCallback(async () => {
    if (!caseId) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', dateTo)
      if (preset) params.set('preset', preset)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/combined-transactions?${params}`))
      const json = await res.json()
      if (json.success) {
        setItems(json.data || [])
      } else {
        setError(json.error || 'Failed to load')
      }
    } catch (e) {
      setError(e.message || 'Failed to load')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [caseId, dateFrom, dateTo, preset])

  useEffect(() => {
    if (isOpen && tab === 'browse' && caseId) loadCombined()
  }, [isOpen, tab, caseId, dateFrom, dateTo, preset])

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set())
    else setSelected(new Set(items.map(i => i.id)))
  }

  const handleAddSelected = async () => {
    if (!caseId || selected.size === 0) return
    setAdding(true)
    setError(null)
    try {
      const bulkItems = []
      for (const id of selected) {
        const it = items.find(x => x.id === id)
        if (!it) continue
        const payload = { ...it }
        delete payload._memberLabel
        bulkItems.push({
          type: it.type === 'session' ? 'session' : 'transaction',
          payload,
          source_table: it.type === 'session' ? 'gaming_sessions' : 'gaming_transactions',
          [it.type === 'session' ? 'SessionPK' : 'TransactionPK']: id
        })
      }
      const res = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: bulkItems })
      })
      const json = await res.json()
      if (json.success) {
        setSelected(new Set())
        onAdded?.()
        onClose?.()
      } else {
        setError(json.error || 'Failed to add')
      }
    } catch (e) {
      setError(e.message || 'Failed to add')
    } finally {
      setAdding(false)
    }
  }

  const handleCsvChange = (e) => {
    const file = e.target?.files?.[0]
    if (!file) return
    setCsvFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result || ''
      const lines = text.split(/\r?\n/).filter(Boolean)
      const headers = lines[0]?.split(/[,;\t]/).map(h => h.trim()) || []
      const rows = lines.slice(1).map(line => {
        const vals = line.split(/[,;\t]/).map(v => v.trim())
        const row = {}
        headers.forEach((h, i) => { row[h] = vals[i] ?? '' })
        return row
      })
      setCsvPreview(rows.slice(0, 20))
    }
    reader.readAsText(file)
  }

  const handleAddFromCsv = async () => {
    if (!caseId || !csvFile) return
    setAdding(true)
    setError(null)
    try {
      const text = await new Promise((res, rej) => {
        const r = new FileReader()
        r.onload = () => res(r.result)
        r.onerror = rej
        r.readAsText(csvFile)
      })
      const lines = String(text).split(/\r?\n/).filter(Boolean)
      const headers = lines[0]?.split(/[,;\t]/).map(h => h.trim().toLowerCase()) || []
      const idx = (n) => headers.findIndex(h => h && h.includes(n))
      const get = (vals, names) => {
        for (const n of names) {
          const i = idx(n)
          if (i >= 0 && vals[i] !== undefined) return vals[i]
        }
        return ''
      }
      const rows = lines.slice(1).map(line => line.split(/[,;\t]/).map(v => v.trim()))
      const itemsToAdd = rows.map((vals, i) => {
        const txId = get(vals, ['transactionpk', 'transaction_id', 'id'])
        const sessId = get(vals, ['sessionpk', 'session_id'])
        const id = txId || sessId || `csv-${i}`
        const type = sessId ? 'session' : 'transaction'
        const payload = {
          [type === 'session' ? 'SessionPK' : 'TransactionPK']: id,
          MemberID: get(vals, ['memberid', 'member_id']) || null,
          Venue: get(vals, ['venue']) || '',
          EgmHouseNo: get(vals, ['egmhouseno', 'egm', 'egm_house_no']) || '',
          SnapShotTime: get(vals, ['snapshottime', 'timestamp', 'date']) || new Date().toISOString(),
          CashIn: Number(get(vals, ['cashin', 'cash_in'])) || 0,
          CashOut: Number(get(vals, ['cashout', 'cash_out'])) || 0,
          Turnover: Number(get(vals, ['turnover'])) || 0,
        }
        return { type, payload, source_table: 'csv', [type === 'session' ? 'SessionPK' : 'TransactionPK']: id }
      }).filter(it => it.payload.TransactionPK || it.payload.SessionPK)

      const res = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      })
      const json = await res.json()
      if (json.success) {
        setCsvFile(null)
        setCsvPreview([])
        onAdded?.()
        onClose?.()
      } else {
        setError(json.error || 'Failed to add from CSV')
      }
    } catch (e) {
      setError(e.message || 'Failed to add from CSV')
    } finally {
      setAdding(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-bold text-slate-800">Link Transaction</h3>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setTab('browse')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold ${tab === 'browse' ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
            style={tab === 'browse' ? { borderBottomColor: P.pri } : {}}
          >
            <Search size={16} /> Browse Combined Transactions
          </button>
          <button
            type="button"
            onClick={() => setTab('csv')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold ${tab === 'csv' ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
            style={tab === 'csv' ? { borderBottomColor: P.pri } : {}}
          >
            <FileUp size={16} /> From CSV
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {error && <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

          {tab === 'browse' && (
            <>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="input-field text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="input-field text-sm" />
                </div>
                <div className="flex gap-1">
                  {['7days', '2weeks', '1month'].map(p => (
                    <button key={p} type="button" onClick={() => setPreset(p)} className={`px-3 py-1.5 text-xs font-semibold rounded border ${preset === p ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
                      {p === '7days' ? '7 days' : p === '2weeks' ? '2 weeks' : '1 month'}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={loadCombined} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
                  <Calendar size={14} /> Apply
                </button>
              </div>

              {loading ? (
                <div className="py-8 text-center text-slate-500">Loading…</div>
              ) : (
                <div className="border rounded overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left">
                          <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={selectAll} />
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">ID</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Type</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Subject</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Amount</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">EGM</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Venue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(it => (
                        <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2"><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)} /></td>
                          <td className="px-3 py-2 font-mono text-xs">{it.id}</td>
                          <td className="px-3 py-2">{it.type === 'session' ? 'Session' : 'Transaction'}</td>
                          <td className="px-3 py-2">{it._memberLabel || it.MemberID || 'Unidentified'}</td>
                          <td className="px-3 py-2">{(it.SnapShotTime || it.StartTime || '').toString().slice(0, 16)}</td>
                          <td className="px-3 py-2 font-bold text-red-600">{fmt$(it.CashOut || it.CashIn || it.Turnover)}</td>
                          <td className="px-3 py-2">{it.EgmHouseNo || it.EgmName || '-'}</td>
                          <td className="px-3 py-2">{it.Venue || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {items.length === 0 && !loading && <div className="py-8 text-center text-slate-500">No sessions or transactions found. Adjust date range or run migration 039 to seed unattributed data.</div>}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button type="button" onClick={handleAddSelected} disabled={selected.size === 0 || adding} className="btn-primary flex items-center gap-2">
                  {adding ? 'Adding…' : `Add ${selected.size} selected`}
                </button>
              </div>
            </>
          )}

          {tab === 'csv' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Upload CSV file</label>
                <input type="file" accept=".csv,.txt" onChange={handleCsvChange} className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700" />
                <p className="text-xs text-slate-500 mt-1">Columns: TransactionPK or SessionPK, MemberID, Venue, EgmHouseNo, SnapShotTime, CashIn, CashOut, Turnover</p>
              </div>
              {csvPreview.length > 0 && (
                <div className="border rounded overflow-x-auto mb-4 max-h-48">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-50"><tr>{Object.keys(csvPreview[0] || {}).map(k => <th key={k} className="px-2 py-1 text-left">{k}</th>)}</tr></thead>
                    <tbody>{csvPreview.map((row, i) => <tr key={i} className="border-t">{Object.values(row).map((v, j) => <td key={j} className="px-2 py-1">{String(v)}</td>)}</tr>)}</tbody>
                  </table>
                  {csvPreview.length >= 20 && <div className="px-2 py-1 text-slate-500 text-xs">Showing first 20 rows</div>}
                </div>
              )}
              <div className="flex justify-end">
                <button type="button" onClick={handleAddFromCsv} disabled={!csvFile || adding} className="btn-primary">
                  {adding ? 'Adding…' : 'Add from CSV'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
